/**
 * Admin API Client
 * Endpoints for company and user management (admin only)
 */

import { apiClient } from "./client";
import type {
  AdminUserCreate,
  AdminUserUpdate,
  Company,
  CompanyCreate,
  CompanyListResponse,
  CompanyUpdate,
  PasswordResetResponse,
  User,
  UserCreateResponse,
  UserListResponse,
} from "./types";

// ============================================================================
// Company Management
// ============================================================================

/**
 * Get all companies
 */
export async function getCompanies(): Promise<CompanyListResponse> {
  return apiClient.get<CompanyListResponse>("/api/v1/admin/companies");
}

/**
 * Get a specific company by ID
 */
export async function getCompany(companyId: number): Promise<Company> {
  return apiClient.get<Company>(`/api/v1/admin/companies/${companyId}`);
}

/**
 * Create a new company
 */
export async function createCompany(data: CompanyCreate): Promise<Company> {
  return apiClient.post<Company>("/api/v1/admin/companies", data);
}

/**
 * Update a company
 */
export async function updateCompany(
  companyId: number,
  data: CompanyUpdate
): Promise<Company> {
  return apiClient.patch<Company>(`/api/v1/admin/companies/${companyId}`, data);
}

/**
 * Delete a company
 */
export async function deleteCompany(companyId: number): Promise<void> {
  return apiClient.delete(`/api/v1/admin/companies/${companyId}`);
}

// ============================================================================
// User Management
// ============================================================================

/**
 * Get all users, optionally filtered by company
 */
export async function getUsers(companyId?: number): Promise<UserListResponse> {
  const params = companyId ? `?company_id=${companyId}` : "";
  return apiClient.get<UserListResponse>(`/api/v1/admin/users${params}`);
}

/**
 * Get a specific user by ID
 */
export async function getUser(userId: number): Promise<User> {
  return apiClient.get<User>(`/api/v1/admin/users/${userId}`);
}

/**
 * Create a new user
 * Returns generated_password if no password was provided
 */
export async function createUser(data: AdminUserCreate): Promise<UserCreateResponse> {
  return apiClient.post<UserCreateResponse>("/api/v1/admin/users", data);
}

/**
 * Update a user
 */
export async function updateUser(
  userId: number,
  data: AdminUserUpdate
): Promise<User> {
  return apiClient.patch<User>(`/api/v1/admin/users/${userId}`, data);
}

/**
 * Reset a user's password
 * Returns a temporary password that should be communicated to the user
 */
export async function resetUserPassword(
  userId: number
): Promise<PasswordResetResponse> {
  return apiClient.post<PasswordResetResponse>(
    `/api/v1/admin/users/${userId}/reset-password`,
    {}
  );
}

/**
 * Reactivate a deactivated user
 */
export async function reactivateUser(userId: number): Promise<User> {
  return apiClient.patch<User>(`/api/v1/admin/users/${userId}`, {
    is_active: true,
  });
}

/**
 * Deactivate a user (soft delete)
 */
export async function deactivateUser(userId: number): Promise<void> {
  return apiClient.delete(`/api/v1/admin/users/${userId}`);
}

/**
 * Enable 2FA for a user (admin/manager action)
 */
export async function enableUser2FA(
  userId: number
): Promise<{ success: boolean; message: string }> {
  return apiClient.post<{ success: boolean; message: string }>(
    `/api/v1/admin/users/${userId}/enable-2fa`,
    {}
  );
}

/**
 * Disable 2FA for a user (admin/manager action)
 */
export async function disableUser2FA(
  userId: number
): Promise<{ success: boolean; message: string }> {
  return apiClient.post<{ success: boolean; message: string }>(
    `/api/v1/admin/users/${userId}/disable-2fa`,
    {}
  );
}
